<template>
  <div v-loading="loading" class="teacher-timeline">
    <div class="teacher-timeline__toolbar">
      <el-radio-group v-model="rangeMode" size="mini" @change="handleRangeModeChange">
        <el-radio-button label="default">默认（过去7天+未来14天）</el-radio-button>
        <el-radio-button label="custom">自定义时间</el-radio-button>
      </el-radio-group>
      <el-date-picker
        v-if="rangeMode === 'custom'"
        v-model="customRange"
        type="datetimerange"
        size="small"
        range-separator="至"
        start-placeholder="开始"
        end-placeholder="结束"
        value-format="yyyy-MM-dd HH:mm:ss"
        format="yyyy-MM-dd HH:mm"
        :default-time="['00:00:00', '23:59:59']"
        class="teacher-timeline__range-picker"
        @change="loadData"
      />
      <el-button size="mini" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      <div class="teacher-timeline__legend">
        <span class="teacher-timeline__legend-item is-empty">空闲</span>
        <span class="teacher-timeline__legend-item is-scheduled">已排课</span>
        <span class="teacher-timeline__legend-item is-ongoing">进行中</span>
        <span class="teacher-timeline__legend-item is-finished">已结束</span>
      </div>
    </div>

    <div class="teacher-timeline__cards">
      <div
        v-for="item in teacherCards"
        :key="item.id"
        class="teacher-timeline__card"
        :class="{ 'is-active': selectedTeacherId === item.id }"
        @click="selectTeacher(item.id)"
      >
        <div class="teacher-timeline__card-name">{{ item.teacherName }}</div>
        <div class="teacher-timeline__card-meta">{{ item.sessionCount }} 节课</div>
      </div>
      <div v-if="!teacherCards.length" class="schools-admin-page__placeholder" style="padding: 24px 0;">暂无教师数据</div>
    </div>

    <div v-if="selectedTeacherId" class="teacher-timeline__board-wrap">
      <div class="teacher-timeline__board-title">
        {{ selectedTeacherName }} · 共 {{ timelineDays.length }} 天
      </div>
      <div class="teacher-timeline__board">
        <div class="teacher-timeline__hour-labels">
          <div class="teacher-timeline__corner" />
          <div v-for="h in 24" :key="'h-' + h" class="teacher-timeline__hour-label">{{ formatHourLabel(h - 1) }}</div>
        </div>
        <div class="teacher-timeline__days-scroll">
          <div v-for="day in timelineDays" :key="day" class="teacher-timeline__day-col">
            <div class="teacher-timeline__day-head" :class="{ 'is-today': isToday(day), 'is-past': isPastDay(day) }">
              <div class="teacher-timeline__day-date">{{ formatDayLabel(day) }}</div>
              <div class="teacher-timeline__day-week">{{ weekdayLabel(day) }}</div>
            </div>
            <div class="teacher-timeline__day-body">
              <div
                v-for="h in 24"
                :key="day + '-' + h"
                class="teacher-timeline__hour-cell"
                :class="hourCellClass(day, h - 1)"
                :title="hourCellTitle(day, h - 1)"
                @click="onHourClick(day, h - 1)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="schools-admin-page__placeholder">请选择上方教师卡片查看排课时间轴</div>

    <el-dialog
      :title="sessionDetailTitle"
      :visible.sync="sessionDetailVisible"
      width="820px"
      top="6vh"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetSessionDetail"
    >
      <div v-loading="sessionDetailLoading">
        <el-tabs
          v-if="hourSessionList.length > 1"
          v-model="activeSessionId"
          type="card"
          class="teacher-session-tabs"
          @tab-click="onSessionTabChange"
        >
          <el-tab-pane
            v-for="s in hourSessionList"
            :key="s.id"
            :label="className(s.classId) + ' ' + formatTime(s.startTime)"
            :name="String(s.id)"
          />
        </el-tabs>

        <template v-if="currentSession">
          <el-descriptions :column="2" size="small" border class="teacher-session-desc">
            <el-descriptions-item label="上课日期">{{ currentSession.sessionDate || '-' }}</el-descriptions-item>
            <el-descriptions-item label="上课时间">{{ formatTime(currentSession.startTime) }} - {{ formatTime(currentSession.endTime) }}</el-descriptions-item>
            <el-descriptions-item label="班级">{{ className(currentSession.classId) }}</el-descriptions-item>
            <el-descriptions-item label="授课教师">{{ teacherName(currentSession.teacherId) }}</el-descriptions-item>
            <el-descriptions-item label="教室">{{ roomName(currentSession.roomId) }}</el-descriptions-item>
            <el-descriptions-item label="课次状态">
              <el-tag size="mini" :type="sessionStatusTagType(currentSession)">{{ visualStatusLabel(currentSession) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="课程主题" :span="2">{{ currentSession.topic || '-' }}</el-descriptions-item>
          </el-descriptions>

          <div class="teacher-session-stats">
            <span>在班 {{ attendanceSummary.total }} 人</span>
            <span class="is-present">到课 {{ attendanceSummary.present }}</span>
            <span class="is-late">迟到 {{ attendanceSummary.late }}</span>
            <span class="is-leave">请假 {{ attendanceSummary.leave }}</span>
            <span class="is-absent">缺勤 {{ attendanceSummary.absent }}</span>
            <span class="is-pending">未点名 {{ attendanceSummary.pending }}</span>
          </div>

          <el-table :data="attendanceRows" size="small" :border="false" style="width: 100%; margin-top: 12px;" max-height="360">
            <el-table-column label="学员" min-width="100">
              <template slot-scope="{ row }">{{ (row.student && row.student.studentName) || '-' }}</template>
            </el-table-column>
            <el-table-column label="学号" min-width="110">
              <template slot-scope="{ row }">{{ (row.student && row.student.studentCode) || '-' }}</template>
            </el-table-column>
            <el-table-column label="家长手机" min-width="120">
              <template slot-scope="{ row }">{{ (row.student && row.student.parentPhone) || '-' }}</template>
            </el-table-column>
            <el-table-column label="参与情况" width="100" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="attendanceStatusTagType(row)">{{ attendanceStatusLabel(row) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="点名时间" width="150">
              <template slot-scope="{ row }">{{ formatDateTime(row.attendance && row.attendance.checkTime) }}</template>
            </el-table-column>
            <el-table-column label="扣课" width="70" align="center">
              <template slot-scope="{ row }">
                <span v-if="row.attendance && row.attendance.lessonDeducted === 1">已扣</span>
                <span v-else style="color:#c0c4cc;">-</span>
              </template>
            </el-table-column>
            <el-table-column label="备注" min-width="100" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ (row.attendance && row.attendance.remark) || '-' }}</template>
            </el-table-column>
          </el-table>
          <div v-if="!attendanceRows.length" class="schools-admin-page__placeholder" style="padding: 20px 0;">该班级暂无在班学员</div>
        </template>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="sessionDetailVisible = false">关闭</el-button>
        <el-button v-if="currentSession && currentSession.status === 'scheduled'" type="primary" size="small" @click="handleEditSession">编辑课次</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import dayjs from 'dayjs'
import { fetchSchoolSessions, fetchSchoolSessionAttendance } from '@/api/h5-admin-schools'

const WEEKDAY = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
const ATTENDANCE_LABEL = {
  present: '到课',
  late: '迟到',
  leave: '请假',
  absent: '缺勤'
}

export default {
  name: 'TeacherScheduleTimeline',
  props: {
    teacherOptions: { type: Array, default: () => [] },
    classOptions: { type: Array, default: () => [] },
    roomOptions: { type: Array, default: () => [] },
    classId: { type: [Number, String], default: null },
    teacherId: { type: [Number, String], default: null },
    status: { type: String, default: null }
  },
  data() {
    return {
      loading: false,
      rangeMode: 'default',
      customRange: null,
      selectedTeacherId: null,
      allSessions: [],
      sessionDetailVisible: false,
      sessionDetailLoading: false,
      hourDetailDay: '',
      hourDetailHour: 0,
      hourSessionList: [],
      activeSessionId: '',
      sessionDetail: null
    }
  },
  computed: {
    queryKey() {
      return [this.classId, this.teacherId, this.status, this.rangeMode, this.customRange && this.customRange.join('|')].join('_')
    },
    dateRange() {
      if (this.rangeMode === 'custom' && this.customRange && this.customRange.length === 2) {
        return [this.customRange[0], this.customRange[1]]
      }
      const today = dayjs().startOf('day')
      return [
        today.subtract(7, 'day').format('YYYY-MM-DD 00:00:00'),
        today.add(14, 'day').format('YYYY-MM-DD 23:59:59')
      ]
    },
    timelineDays() {
      const start = dayjs(String(this.dateRange[0]).slice(0, 10))
      const end = dayjs(String(this.dateRange[1]).slice(0, 10))
      const days = []
      let cur = start
      while (cur.isBefore(end) || cur.isSame(end, 'day')) {
        days.push(cur.format('YYYY-MM-DD'))
        cur = cur.add(1, 'day')
      }
      return days
    },
    filteredSessions() {
      let list = this.allSessions || []
      if (this.status) list = list.filter(s => s.status === this.status)
      if (this.classId) list = list.filter(s => s.classId === this.classId)
      if (this.teacherId) list = list.filter(s => s.teacherId === this.teacherId)
      return list.filter(s => s.status !== 'cancelled')
    },
    teacherCards() {
      const map = {}
      ;(this.teacherOptions || []).forEach(t => {
        if (this.teacherId && t.id !== this.teacherId) return
        map[t.id] = { id: t.id, teacherName: t.teacherName, sessionCount: 0 }
      })
      this.filteredSessions.forEach(s => {
        if (!s.teacherId) return
        if (!map[s.teacherId]) {
          map[s.teacherId] = { id: s.teacherId, teacherName: '教师#' + s.teacherId, sessionCount: 0 }
        }
        map[s.teacherId].sessionCount++
      })
      return Object.values(map).sort((a, b) => b.sessionCount - a.sessionCount || String(a.teacherName).localeCompare(b.teacherName))
    },
    selectedTeacherSessions() {
      if (!this.selectedTeacherId) return []
      return this.filteredSessions.filter(s => s.teacherId === this.selectedTeacherId)
    },
    selectedTeacherName() {
      const t = (this.teacherOptions || []).find(x => x.id === this.selectedTeacherId)
      return t ? t.teacherName : ('教师#' + this.selectedTeacherId)
    },
    currentSession() {
      if (this.sessionDetail && this.sessionDetail.session) return this.sessionDetail.session
      const id = Number(this.activeSessionId)
      return this.hourSessionList.find(s => s.id === id) || null
    },
    attendanceRows() {
      return (this.sessionDetail && this.sessionDetail.rows) || []
    },
    attendanceSummary() {
      const summary = { total: 0, present: 0, late: 0, leave: 0, absent: 0, pending: 0 }
      this.attendanceRows.forEach(row => {
        summary.total++
        const st = row.attendance && row.attendance.status
        if (!st) summary.pending++
        else if (summary[st] != null) summary[st]++
      })
      return summary
    },
    sessionDetailTitle() {
      if (!this.hourDetailDay) return '课次详情'
      const label = this.formatHourLabel(this.hourDetailHour)
      if (this.hourSessionList.length === 1 && this.currentSession) {
        return this.className(this.currentSession.classId) + ' · ' + this.hourDetailDay + ' ' + label
      }
      return this.hourDetailDay + ' ' + label + ' 课次详情'
    }
  },
  watch: {
    queryKey() {
      this.loadData()
    },
    teacherCards(list) {
      if (!list.length) {
        this.selectedTeacherId = null
        return
      }
      if (!this.selectedTeacherId || !list.find(x => x.id === this.selectedTeacherId)) {
        this.selectedTeacherId = list[0].id
      }
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    handleRangeModeChange() {
      if (this.rangeMode === 'default') this.customRange = null
      this.loadData()
    },
    loadData() {
      this.loading = true
      const params = {
        dateFrom: String(this.dateRange[0]).slice(0, 10),
        dateTo: String(this.dateRange[1]).slice(0, 10),
        page: 0,
        size: 2000
      }
      if (this.classId) params.classId = this.classId
      fetchSchoolSessions(params).then(res => {
        this.allSessions = (res && res.content) || []
      }).finally(() => { this.loading = false })
    },
    selectTeacher(id) {
      this.selectedTeacherId = id
    },
    className(id) {
      const c = (this.classOptions || []).find(x => x.id === id)
      return c ? c.className : (id || '-')
    },
    teacherName(id) {
      const t = (this.teacherOptions || []).find(x => x.id === id)
      return t ? t.teacherName : (id || '-')
    },
    roomName(id) {
      const r = (this.roomOptions || []).find(x => x.id === id)
      return r ? r.roomName : (id ? '教室#' + id : '-')
    },
    formatTime(t) {
      if (!t) return '-'
      return String(t).substring(0, 5)
    },
    formatDateTime(v) {
      if (!v) return '-'
      return dayjs(v).format('YYYY-MM-DD HH:mm')
    },
    parseMinutes(t) {
      const parts = this.formatTime(t).split(':')
      return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0)
    },
    formatHourLabel(h) {
      return (h < 10 ? '0' : '') + h + ':00'
    },
    formatDayLabel(day) {
      return day ? day.slice(5) : ''
    },
    weekdayLabel(day) {
      return WEEKDAY[dayjs(day).day()]
    },
    isToday(day) {
      return day === dayjs().format('YYYY-MM-DD')
    },
    isPastDay(day) {
      return dayjs(day).isBefore(dayjs(), 'day')
    },
    sessionVisualStatus(session) {
      if (!session || session.status === 'cancelled') return 'empty'
      if (session.status === 'finished') return 'finished'
      const now = dayjs()
      const start = dayjs(session.sessionDate + ' ' + this.formatTime(session.startTime))
      const end = dayjs(session.sessionDate + ' ' + this.formatTime(session.endTime))
      if (now.isAfter(end)) return 'finished'
      if (!now.isBefore(start) && now.isBefore(end)) return 'ongoing'
      return 'scheduled'
    },
    visualStatusLabel(session) {
      const map = { scheduled: '待上课', ongoing: '进行中', finished: '已结束', empty: '空闲', cancelled: '已取消' }
      const key = session && session.status === 'cancelled' ? 'cancelled' : this.sessionVisualStatus(session)
      return map[key] || '-'
    },
    sessionStatusTagType(session) {
      const st = this.sessionVisualStatus(session)
      if (st === 'ongoing') return 'success'
      if (st === 'scheduled') return ''
      if (st === 'finished') return 'info'
      return 'info'
    },
    attendanceStatusLabel(row) {
      const st = row.attendance && row.attendance.status
      return st ? (ATTENDANCE_LABEL[st] || st) : '未点名'
    },
    attendanceStatusTagType(row) {
      const st = row.attendance && row.attendance.status
      if (!st) return 'info'
      const map = { present: 'success', late: 'warning', leave: '', absent: 'danger' }
      return map[st] || 'info'
    },
    sessionOverlapsHour(session, hour) {
      const startMin = this.parseMinutes(session.startTime)
      const endMin = this.parseMinutes(session.endTime)
      const hourStart = hour * 60
      const hourEnd = (hour + 1) * 60
      return startMin < hourEnd && endMin > hourStart
    },
    sessionsInHour(day, hour) {
      return this.selectedTeacherSessions.filter(s => s.sessionDate === day && this.sessionOverlapsHour(s, hour))
    },
    hourCellClass(day, hour) {
      const sessions = this.sessionsInHour(day, hour)
      if (!sessions.length) return 'is-empty'
      let rank = 0
      const priority = { finished: 1, scheduled: 2, ongoing: 3 }
      sessions.forEach(s => {
        const st = this.sessionVisualStatus(s)
        if (priority[st] > rank) rank = priority[st]
      })
      if (rank === 3) return 'is-ongoing'
      if (rank === 2) return 'is-scheduled'
      return 'is-finished'
    },
    hourCellTitle(day, hour) {
      const sessions = this.sessionsInHour(day, hour)
      if (!sessions.length) return this.formatHourLabel(hour) + ' 无课'
      return sessions.map(s => this.className(s.classId) + ' ' + this.formatTime(s.startTime) + '-' + this.formatTime(s.endTime)).join('\n')
    },
    onHourClick(day, hour) {
      const sessions = this.sessionsInHour(day, hour)
      if (!sessions.length) return
      this.hourDetailDay = day
      this.hourDetailHour = hour
      this.hourSessionList = sessions
      this.activeSessionId = String(sessions[0].id)
      this.sessionDetailVisible = true
      this.loadSessionDetail(sessions[0].id)
    },
    onSessionTabChange() {
      const id = Number(this.activeSessionId)
      if (id) this.loadSessionDetail(id)
    },
    loadSessionDetail(sessionId) {
      if (!sessionId) return
      this.sessionDetailLoading = true
      this.sessionDetail = null
      fetchSchoolSessionAttendance(sessionId).then(res => {
        this.sessionDetail = res || null
      }).finally(() => {
        this.sessionDetailLoading = false
      })
    },
    resetSessionDetail() {
      this.hourDetailDay = ''
      this.hourDetailHour = 0
      this.hourSessionList = []
      this.activeSessionId = ''
      this.sessionDetail = null
    },
    handleEditSession() {
      if (!this.currentSession) return
      this.sessionDetailVisible = false
      this.$emit('edit', this.currentSession)
    }
  }
}
</script>
<style scoped lang="less">
.teacher-timeline__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.teacher-timeline__range-picker {
  width: 360px;
}
.teacher-timeline__legend {
  margin-left: auto;
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #606266;
}
.teacher-timeline__legend-item {
  display: inline-flex;
  align-items: center;
  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
  }
  &.is-empty::before { background: #e4e7ed; }
  &.is-scheduled::before { background: #409eff; }
  &.is-ongoing::before { background: #67c23a; }
  &.is-finished::before { background: #b8c4b0; }
}

.teacher-timeline__cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.teacher-timeline__card {
  min-width: 120px;
  padding: 10px 14px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #c6e2ff; box-shadow: 0 2px 8px rgba(64, 158, 255, 0.12); }
  &.is-active {
    border-color: #409eff;
    background: #ecf5ff;
    box-shadow: 0 2px 10px rgba(64, 158, 255, 0.18);
  }
}
.teacher-timeline__card-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.teacher-timeline__card-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.teacher-timeline__board-wrap {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}
.teacher-timeline__board-title {
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
  background: #fafafa;
}
.teacher-timeline__board {
  display: flex;
  max-height: 520px;
  overflow: auto;
}
.teacher-timeline__hour-labels {
  flex: 0 0 52px;
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fafafa;
  border-right: 1px solid #ebeef5;
}
.teacher-timeline__corner {
  height: 48px;
  border-bottom: 1px solid #ebeef5;
  position: sticky;
  top: 0;
  z-index: 3;
  background: #fafafa;
}
.teacher-timeline__hour-label {
  height: 14px;
  line-height: 14px;
  font-size: 10px;
  color: #909399;
  text-align: right;
  padding-right: 6px;
  box-sizing: border-box;
}
.teacher-timeline__days-scroll {
  display: flex;
  min-width: min-content;
}
.teacher-timeline__day-col {
  flex: 0 0 44px;
  border-right: 1px solid #f0f2f5;
}
.teacher-timeline__day-head {
  height: 48px;
  padding: 4px 2px;
  text-align: center;
  border-bottom: 1px solid #ebeef5;
  background: #fafafa;
  position: sticky;
  top: 0;
  z-index: 1;
  &.is-today {
    background: #ecf5ff;
    .teacher-timeline__day-date { color: #409eff; font-weight: 700; }
  }
  &.is-past .teacher-timeline__day-date { color: #909399; }
}
.teacher-timeline__day-date {
  font-size: 12px;
  line-height: 16px;
  color: #303133;
}
.teacher-timeline__day-week {
  font-size: 10px;
  color: #909399;
  line-height: 14px;
}
.teacher-timeline__day-body {
  display: flex;
  flex-direction: column;
}
.teacher-timeline__hour-cell {
  height: 14px;
  box-sizing: border-box;
  border-bottom: 1px solid #f5f7fa;
  cursor: pointer;
  transition: opacity 0.12s;
  &:hover { opacity: 0.85; }
  &.is-empty { background: #ebeef3; cursor: default; }
  &.is-scheduled { background: #409eff; }
  &.is-ongoing { background: #67c23a; }
  &.is-finished { background: #b8c4b0; }
}

.teacher-session-tabs {
  margin-bottom: 12px;
}
.teacher-session-desc {
  margin-bottom: 12px;
}
.teacher-session-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 13px;
  color: #606266;
  padding: 8px 0 4px;
  span { white-space: nowrap; }
  .is-present { color: #67c23a; }
  .is-late { color: #e6a23c; }
  .is-leave { color: #909399; }
  .is-absent { color: #f56c6c; }
  .is-pending { color: #c0c4cc; }
}
</style>
